import { INVOICE_GENERATION_KIND_LABELS, sowScopeLabel } from '@clockwise/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { formatMoney } from '@/components/custom/invoice-money';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { runStatusCopy, useRuns } from '@/lib/invoice-run-hooks';
import { lastSearch } from '@/lib/last-search';

/**
 * Generation run history.
 *
 * A DIAGNOSTIC page, not a daily job — which is why it is reached from the
 * invoice list's overflow menu rather than the sidebar. Nobody works from here.
 * People open it when the numbers look wrong, when a customer asks why they
 * weren't billed, or when the month's total doesn't match last month's.
 *
 * The month-over-month read is the point: three rows of
 * `Scheduled · August 2026 · 212 · $2.1M` make a month that raised $1.4M
 * obvious at a glance, which no per-customer surface can show.
 *
 * Static `runs` segment, so it wins over `$id` in the router's match order —
 * same reason `invoices.new.tsx` does.
 */
export const Route = createFileRoute('/_authenticated/invoices/runs/')({
  component: RunHistoryPage,
});

function RunHistoryPage() {
  const { data, isPending, isError, error } = useRuns({ limit: 50, offset: 0 });
  const runs = data?.data ?? [];

  return (
    <PageShell>
      <PageHeader
        title="Generation runs"
        subtitle="Every invoice generation the system has performed."
        count={data?.meta.total}
      >
        <Button asChild variant="outline">
          <Link to="/invoices" search={lastSearch('/invoices')}>
            Back to invoices
          </Link>
        </Button>
      </PageHeader>

      {isError ? (
        <div className="rounded-lg border border-destructive/50 p-4" role="alert">
          <p className="text-destructive text-sm">
            {error instanceof Error ? error.message : "Couldn't load the run history."}
          </p>
        </div>
      ) : isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : runs.length === 0 ? (
        <div className="rounded-lg border p-6 text-center">
          <p className="font-medium text-sm">No generation runs yet</p>
          <p className="mt-1 text-muted-foreground text-sm">
            Press “Generate invoices” on the invoice list to raise a batch.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <Th>Started</Th>
                <Th>Month</Th>
                <Th>Trigger</Th>
                <Th>Scope</Th>
                <Th>Status</Th>
                <Th align="right">Created</Th>
                <Th align="right">Failed</Th>
                <Th align="right">Total</Th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const status = runStatusCopy(run.status);
                return (
                  <tr key={run.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-3 py-2.5">
                      <Link
                        className="font-medium hover:underline"
                        params={{ runId: run.id }}
                        to="/invoices/runs/$runId"
                      >
                        {formatStarted(run.startedAt ?? run.createdAt)}
                      </Link>
                      <div className="text-muted-foreground text-xs">
                        {formatDuration(run.startedAt, run.finishedAt)}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">{monthLabel(run.invoiceMonth)}</td>
                    <td className="px-3 py-2.5">
                      {run.trigger === 'scheduled' ? 'Scheduled' : 'On demand'}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{scopeLabel(run)}</td>
                    <td className="px-3 py-2.5">
                      <Badge
                        variant={
                          status.tone === 'success'
                            ? 'secondary'
                            : status.tone === 'warning'
                              ? 'destructive'
                              : 'outline'
                        }
                      >
                        {status.label}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{run.createdCount}</td>
                    <td
                      className={`px-3 py-2.5 text-right tabular-nums ${
                        run.failedCount > 0 ? 'text-destructive' : ''
                      }`}
                    >
                      {run.failedCount}
                    </td>
                    {/* `formatMoney` returns null for anything it cannot
                        honestly render. "Unknown" rather than an em dash: a
                        dash reads as a real formatted figure, and a missing
                        total is a data gap someone should see (G15). */}
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {formatMoney(run.createdTotal) ?? 'Unknown'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2 font-medium text-muted-foreground ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

/** The run's scope in SOW words. The stored columns kept their old names;
 *  `sowScopeLabel` is the one place that translation happens, so this page and
 *  the run detail cannot describe the same run differently. */
/**
 * What the run covered.
 *
 * A T&M run has no scope to describe — it has one shape of work to do, and its
 * filter columns are always empty, which `sowScopeLabel` would render as "All
 * engagements". That reads as a retainer run over everything, which is a
 * different run entirely. Naming the engine is the honest answer.
 */
function scopeLabel(run: {
  runKind: 'retainer' | 'tm';
  customerTypes: string[];
  invoiceCadences: string[];
}): string {
  if (run.runKind === 'tm') return INVOICE_GENERATION_KIND_LABELS.tm;
  return sowScopeLabel({ lineTypes: run.customerTypes, frequencies: run.invoiceCadences });
}

function monthLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatStarted(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** An em dash while a run is still going — an elapsed time that keeps growing
 *  would imply the page is live when it is polled, not streamed. */
function formatDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const seconds = Math.round(ms / 1000);
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}
